import { HttpErrorResponse } from '@angular/common/http';
import { Product, ProductImage } from './product.model';

export type PromotionalCodeDiscountType = 'percentage' | 'fixed';

export interface PromotionalCode {
  id: number;
  code: string;
  description?: string | null;
  discount_type?: PromotionalCodeDiscountType;
  discount_value?: number;
  [key: string]: unknown;
}

export interface PsDelivery {
  id: number;
  price: number;
  min_for_free: number;
  free_delivery: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PartnerShop {
  id: number;
  name: string;
  price: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CartLineProduct {
  id: number;
  name: string;
  stock: number;
  enabled: boolean;
  /** Tipo de producto. Llega serializado en la relación `lines.product`. */
  product_type?: string;
  images?: ProductImage[];
  /**
   * Restricción por Perrón Points (R4). Llegan por la vía de MODELO CRUDO
   * (columna + `$appends` del backend), así que `early_access` viene como
   * TINYINT `0`/`1`, no como booleano — de ahí el tipo ancho. El estado
   * bloqueado de la línea se DERIVA de estos campos en cada lectura del
   * carrito; no se persiste en ninguna parte.
   */
  early_access?: boolean | number | null;
  early_access_min_points?: number | string | null;
  /** Umbral resuelto: `null` = producto no restringido. */
  early_access_threshold?: number | string | null;
}

export interface CartLine {
  id: number;
  cart_id: number;
  product_id: number;
  quantity: number;
  sale_price: number;
  subtotal: number;
  tax: number;
  taxes: number;
  total: number;
  discount: number;
  /** PVP de lista unitario (neto, pre-descuento). DECIMAL → puede llegar como string. */
  list_sale_price?: number | string;
  /** Flag de oferta del producto en esta línea. */
  offer_discount?: boolean;
  /** Porcentaje de descuento de oferta (0–100). */
  offer_discount_qty?: number;
  promotional_code_id: number | null;
  name: string;
  description: string;
  reference: string;
  internal_reference: string;
  product?: CartLineProduct;
  created_at?: string;
  updated_at?: string;
}

export interface Cart {
  id: number;
  client_id: number | null;
  token: string;
  preordered: boolean;
  subtotal: number;
  taxes: number;
  total: number;
  delivery: number;
  discounts: number;
  promotional_code_id: number | null;
  productsQuantity: number;
  lines: CartLine[];
  promotional_code: PromotionalCode | null;
  ps_delivery: PsDelivery | null;
  partner_shops: PartnerShop[];
  no_apply_for_intershop: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface NewCartLineRequest {
  product_id: number;
  quantity: number;
  token?: string | null;
}

export interface UpdateCartLineRequest {
  product_id: number;
  quantity: number;
  token: string;
}

export interface PromoCodeRequest {
  code: string;
  cart_id: number;
  token: string;
}

/** Cuerpo de los DELETE que la API autoriza con el token del carrito. */
export interface CartTokenRequest {
  token: string;
}

export type PromoCodeErrorReason =
  | 'invalid'
  | 'expired'
  | 'min_amount'
  | 'not_applicable'
  | 'cart_expired'
  | 'unknown';

/**
 * `error` del 404 con el que la API responde a un token de carrito ajeno o
 * inexistente (IDOR, tramo 1). Contrato con la API, no copy de usuario. ÚNICO
 * sitio del literal: el resto del código pregunta con `isCartGone`.
 */
export const CART_NOT_FOUND_ERROR = 'No existe ningún carrito con ese token';

/** `true` solo para el 404 «carrito no encontrado»; otros 404/422 no cuentan. */
export function isCartGone(err: unknown): boolean {
  return (
    err instanceof HttpErrorResponse &&
    err.status === 404 &&
    err.error?.error === CART_NOT_FOUND_ERROR
  );
}

export class CartStockError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CartStockError';
  }
}

export class CartGoneError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CartGoneError';
  }
}

export class PromoCodeError extends Error {
  constructor(public readonly reason: PromoCodeErrorReason, message: string) {
    super(message);
    this.name = 'PromoCodeError';
  }
}

/** Algunos endpoints devuelven el carrito como `res.data`, otros como el objeto directo. */
export type CartEnvelope = Cart | { data: Cart };

export function unwrapCart(payload: CartEnvelope): Cart {
  if (payload && typeof payload === 'object' && 'data' in payload && (payload as { data: unknown }).data) {
    const maybe = (payload as { data: Cart }).data;
    if (maybe && typeof maybe === 'object' && 'id' in maybe) return maybe;
  }
  return payload as Cart;
}

// ── Preorder ─────────────────────────────────────────────────────────────

/** Métodos de envío que acepta el backend. Mismos valores que el form. */
export type PreorderDeliveryMethod =
  | 'ps'
  | 'uber'
  | 'other_shop'
  | 'home'
  | 'ps_delivery';

/**
 * Métodos de pago que acepta el backend. El checkout sólo ofrece
 * `sinpe`/`transfer`/`online`/`layaway`; `shop`/`later` se conservan por
 * compatibilidad con preorders antiguos que aún los traen.
 */
export type PreorderPaymentMethod =
  | 'shop'
  | 'transfer'
  | 'online'
  | 'later'
  | 'layaway'
  | 'sinpe';

/**
 * Payload que enviamos a `POST /cart-to-preorder/{cartId}`.
 *
 * Reglas condicionales:
 *  - `payment_method` se envía SIEMPRE para que el backend preserve la
 *    distinción entre `transfer` / `layaway` / `online` en delivery.
 *  - `online_payment` se mantiene como conveniencia para código backend
 *    que todavía lo lee; solo se envía en ramas de delivery (no pickup).
 *  - `shop_name` SOLO si `delivery_method === 'other_shop'`.
 *  - Los 5 campos de dirección (province, canton, district, hood,
 *    directions) son nullable; el backend solo exige obligatoriedad si
 *    `delivery_method ∈ {home, ps_delivery}`.
 *
 * `address` legacy NUNCA se envía desde el checkout nuevo (siempre
 * `null`); el backend lo conserva como nullable para retrocompatibilidad
 * con clientes antiguos.
 */
export interface PreorderRequest {
  name: string;
  surnames: string;
  email: string;
  phone: string | null;
  identification: null;
  province: string | null;
  canton: string | null;
  district: string | null;
  hood: string | null;
  directions: string | null;
  address: null;
  delivery_method: PreorderDeliveryMethod;
  delivery_price: number;
  shop_name: string | null;
  payment_method: PreorderPaymentMethod;
  /**
   * Modalidad apartado (flag propio, distinto del método real). El backend lo
   * exige como `'1'`/`'0'` en multipart; el servicio lo serializa así.
   */
  is_layaway: boolean;
  online_payment?: boolean;
  promotional_code_id: number | null;
  discount: number;
  /**
   * Token del HOLD de stock abierto en la puerta (`POST /checkout/{cartId}/reserve`), para el checkout
   * MANUAL. OPCIONAL: si viaja, el backend consume el hold con gracia bajo lock; si no, cae al
   * pre-check raw (backward-compat). Spec backend:
   * perronstore-api/docs/specs/checkout-stock-flotante-pedidos-plan.md.
   */
  session_token?: string | null;
  /**
   * Token del carrito. Viaja SIEMPRE en el body (JSON o multipart), nunca en query. La API lo exige
   * en `checkout/{cart}/reserve`, `checkout/{cart}/payment-intent` y en `cart-to-preorder` (rama
   * legacy). Lo añaden `CartService.cartToPreorder` y `PaymentService`; el checkout no lo rellena.
   * Spec backend: perronstore-api/docs/specs/cart-to-preorder-token-plan.md §6.2.
   */
  token?: string | null;
}

/**
 * Estados que maneja el backend para un Preorder. Vienen de la columna
 * `preorders.state` con default `'nuevo'` (migration 2023_05_05_155057).
 * El front los mapea a variants de badge en la vista de detalle.
 */
export type PreorderState =
  | 'nuevo'
  | 'confirmado'
  | 'preparando'
  | 'listo'
  | 'preparado'
  | 'repartiendo'
  | 'entregado';

/** Cliente embebido dentro del Preorder (relación `client`). */
export interface PreorderClient {
  id: number;
  name: string;
  surnames: string;
  email: string;
  phone: string | null;
  identification?: string | null;
  address?: string | null;
  province?: string | null;
  canton?: string | null;
  district?: string | null;
  hood?: string | null;
  directions?: string | null;
  [key: string]: unknown;
}

/**
 * Pago asociado al Preorder (relación `payments`, modelo `PreorderReceipt`
 * en backend). El modelo backend es minimalista: monto + timestamps. No hay
 * método ni referencia separada — todo lo que sabe el cliente es cuánto y
 * cuándo se registró el pago.
 */
export interface PreorderPayment {
  id: number;
  amount: number | string;
  created_at: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** Producto embebido dentro de cada PreorderLine (relación `lines.product`). */
export interface PreorderLineProduct {
  id: number;
  name: string;
  slug?: string;
  product_type?: string;
  images?: ProductImage[];
  categories?: Product['categories'];
  [key: string]: unknown;
}

/** Línea del Preorder. Backend serializa todos los importes en centavos. */
export interface PreorderLine {
  id: number;
  preorder_id: number;
  product_id: number;
  name: string;
  reference?: string | null;
  /** Referencia interna; en líneas de delivery vale storepickup/uber/ps_delivery/Intertienda/Paquetería. */
  internal_reference?: string | null;
  quantity: number;
  sale_price: number | string;
  subtotal: number | string;
  discount: number | string;
  /** Tipo de descuento (`fixed`/`percent`) si aplica promo. */
  discountType?: string | null;
  /** Monto del impuesto absoluto. */
  tax: number | string;
  /** Tasa del impuesto en porcentaje (p. ej. 13). */
  taxes: number | string;
  total: number | string;
  promotional_code_id: number | null;
  product?: PreorderLineProduct;
  promotional_code?: PromotionalCode | null;
  [key: string]: unknown;
}

/**
 * Respuesta del backend para un Preorder. Tipamos los campos que consume
 * tanto la página de éxito post-checkout como la vista de detalle accesible
 * desde el email (`/pedido/:token`). El indexer `[key: string]: unknown` se
 * mantiene para tolerar campos no críticos sin tocar este archivo.
 */
export interface Preorder {
  id: number;
  token: string;
  state?: PreorderState;
  date?: string;
  due_date?: string | null;
  /**
   * Backend puede devolver el total como número o como string ya formateado
   * (ej. `"CRC 1,500"`). `toAmount()` en el componente lo normaliza a número.
   */
  total: number | string | null;
  subtotal?: number | string | null;
  taxes?: number | string | null;
  discount?: number | string | null;
  paid?: number | string | null;
  delivery_method: PreorderDeliveryMethod;
  delivery_price: number | string | null;
  payment_method?: PreorderPaymentMethod | null;
  /** URL del comprobante subido (SINPE/transferencia). Solo backoffice; el web no lo renderiza. */
  payment_proof_url?: string | null;
  email: string;
  phone: string | null;
  province: string | null;
  canton: string | null;
  district: string | null;
  hood: string | null;
  directions: string | null;
  address: string | null;
  shop_name: string | null;
  comments?: string | null;
  from_prepurchase?: boolean;
  lines?: PreorderLine[];
  client?: PreorderClient;
  payments?: PreorderPayment[];
  promotional_code?: PromotionalCode | null;
  created_at: string;
  [key: string]: unknown;
}

export type PreorderEnvelope = Preorder | { data: Preorder };

export function unwrapPreorder(payload: PreorderEnvelope): Preorder {
  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    (payload as { data: unknown }).data
  ) {
    const maybe = (payload as { data: Preorder }).data;
    if (maybe && typeof maybe === 'object' && 'id' in maybe) return maybe;
  }
  return payload as Preorder;
}

/**
 * Razones por las que el backend rechaza el preorder. `stock` es el 422 raw legacy; los tres
 * `stock_*` son el 409 UNIFORME de la puerta de stock (secuestro por sesión de pago, distinguido por
 * el campo `error`): held (unidades en proceso de pago), out (agotado), insufficient (stock < qty).
 */
export type PreorderErrorReason =
  | 'stock'
  | 'stock_held'
  | 'stock_out'
  | 'stock_insufficient'
  | 'max_per_client'
  | 'promo'
  | 'validation'
  | 'network'
  | 'unknown';

export class PreorderError extends Error {
  constructor(
    public readonly reason: PreorderErrorReason,
    message: string,
  ) {
    super(message);
    this.name = 'PreorderError';
  }
}

/**
 * Entrada del desglose por producto del 409 de la puerta de stock (`POST /checkout/{cart}/reserve` y
 * `/payment-intent`). El backend devuelve una entrada por cada línea del carrito en conflicto; el
 * front reabre el cart drawer y resalta esas líneas ofreciendo ajustar (dejar `available` unidades) o
 * eliminar (cuando `available` es 0). `available` son las unidades LIBRES derivadas (stock − holds
 * vigentes de otras sesiones), nunca el stock crudo. Contrato backend:
 * perronstore-api/docs/api/onvo-pay-payment-intent.md.
 */
export interface StockConflictItem {
  product_id: number;
  name: string | null;
  requested: number;
  available: number;
  reason?: 'stock_out' | 'stock_insufficient' | 'stock_held';
}

export type { Product };
